import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useRef, useState } from 'react';
import { parseCatalogFormSchema } from '../../services/serviceCatalogApi';
import { CatalogFormFieldDraft } from '../../types/CatalogFormFieldDraft';
import { serializeCatalogFormSchema, toCatalogFormFieldDrafts } from '../../utils/catalogFormSchema';
import { CatalogFormSchemaFieldsEditor } from './CatalogFormSchemaFieldsEditor';

type CatalogFormSchemaEditorProps = {
  label?: string;
  description?: string;
  value?: string | null;
  onChange: (json: string) => void;
  disabled?: boolean;
};

function normalizeJsonValue(json?: string | null): string {
  return json?.trim() ? json : '';
}

export function CatalogFormSchemaEditor({
  label = 'Campos do formulário',
  description = 'Defina os campos que o solicitante preencherá ao abrir um chamado deste catálogo.',
  value,
  onChange,
  disabled = false,
}: CatalogFormSchemaEditorProps) {
  const [fields, setFields] = useState<CatalogFormFieldDraft[]>(() =>
    toCatalogFormFieldDrafts(parseCatalogFormSchema(value)?.fields ?? []),
  );
  const [expanded, setExpanded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const lastEmittedJsonRef = useRef(normalizeJsonValue(value));

  useEffect(() => {
    const normalized = normalizeJsonValue(value);
    if (normalized === lastEmittedJsonRef.current) {
      return;
    }

    lastEmittedJsonRef.current = normalized;
    setFields(toCatalogFormFieldDrafts(parseCatalogFormSchema(value)?.fields ?? []));
  }, [value]);

  const summary = useMemo(() => {
    const parsed = parseCatalogFormSchema(value);
    if (!parsed?.fields.length) {
      return 'Nenhum campo definido';
    }
    const labels = parsed.fields
      .slice(0, 3)
      .map((field) => field.label || field.name)
      .join(' · ');
    return `${parsed.fields.length} campo(s): ${labels}`;
  }, [value]);

  function applyFields(nextFields: CatalogFormFieldDraft[]): void {
    setFields(nextFields);
    const json = serializeCatalogFormSchema(nextFields);
    lastEmittedJsonRef.current = json;
    onChange(json);
  }

  return (
    <div className="rounded-xl border border-secondary-200 bg-secondary-50/40">
      <div className="flex flex-wrap items-start justify-between gap-3 p-3">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-start gap-2 text-left"
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? (
            <ChevronDownIcon className="mt-0.5 h-4 w-4 shrink-0 text-secondary-500" />
          ) : (
            <ChevronRightIcon className="mt-0.5 h-4 w-4 shrink-0 text-secondary-500" />
          )}
          <span className="min-w-0">
            <span className="block text-sm font-medium text-secondary-900">{label}</span>
            {description ? (
              <span className="mt-0.5 block text-xs text-secondary-500">{description}</span>
            ) : null}
            {!expanded ? <span className="mt-1 block text-xs text-secondary-600">{summary}</span> : null}
          </span>
        </button>

        <div className="erp-btn-actions shrink-0">
          <button
            type="button"
            className="erp-btn-secondary erp-btn-sm"
            disabled={disabled}
            onClick={() => setModalOpen(true)}
          >
            Abrir editor
          </button>
        </div>
      </div>

      {expanded ? (
        <div className="border-t border-secondary-200 bg-white px-3 py-3">
          <CatalogFormSchemaFieldsEditor fields={fields} disabled={disabled} onChange={applyFields} />
        </div>
      ) : null}

      {modalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="erp-card flex max-h-[90vh] w-full max-w-3xl flex-col shadow-xl" role="dialog" aria-modal="true">
            <div className="border-b border-secondary-100 px-5 py-4">
              <h2 className="erp-card-title">{label}</h2>
              {description ? <p className="erp-card-subtitle mt-1">{description}</p> : null}
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              <CatalogFormSchemaFieldsEditor fields={fields} disabled={disabled} onChange={applyFields} />
            </div>
            <div className="erp-btn-actions border-t border-secondary-100 px-5 py-4">
              <button type="button" className="erp-btn-primary" onClick={() => setModalOpen(false)}>
                Concluir
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
